import React from "react"
import { Form, useNavigate, useSearchParams } from "react-router-dom"
import { Api } from "../lib/api"
import { unrollError } from "../lib/util"

import {
  Alert,
  Button,
  FormControl,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"

import { CurrentOrgId } from "../lib/store"
import GradientBox from "./GradientBox"

// After org creation, send users into the app. Subscription is enforced in
// AuthRequired (redirect to /choose-plan when inactive).
const POST_ACTIVATION_REDIRECT_URL = "/datasets?product_tour_id=528227"

export default function OrganizationCreate() {
  const [error, setError] = React.useState(null)
  const [sessionData, setSessionData] = React.useState(null)
  const [sessionLoading, setSessionLoading] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = new Api()
  const currentOrgId = CurrentOrgId.getValue()
  const sessionId = searchParams.get("session_id")
  const formBusy = sessionLoading || submitting

  React.useEffect(() => {
    // If the user already has an organization, redirect them to the main application
    // This prevents users from creating multiple organizations and handles direct
    // navigation to this page when an organization already exists
    if (currentOrgId) {
      console.log(
        "User already has an organization, redirecting to",
        POST_ACTIVATION_REDIRECT_URL,
      )
      navigate(POST_ACTIVATION_REDIRECT_URL)
    }
  }, [currentOrgId])

  React.useEffect(() => {
    if (currentOrgId) return

    if (!sessionId) {
      // No session_id and no org — user hasn't paid yet, send them to choose a plan
      console.log("No session_id and no org, redirecting to /choose-plan")
      navigate("/choose-plan")
      return
    }

    const fetchSession = async () => {
      setSessionLoading(true)
      try {
        const data = await api.stripeGetCheckoutSession(sessionId)
        setSessionData(data)
      } catch (e) {
        console.error("Failed to fetch checkout session", e)
        setError("Could not verify your subscription. Please contact support.")
      } finally {
        setSessionLoading(false)
      }
    }

    fetchSession()
  }, [sessionId, currentOrgId])

  async function handleSubmit(e) {
    e.preventDefault()
    if (submitting || sessionLoading) return
    setSubmitting(true)
    setError(null)
    try {
      // Create the organization with the Stripe session ID so the backend can
      // link the subscription immediately
      const org = await api.organizationsCreate(e.target.name.value, sessionId)
      const orgs = [org]
      api.setOrFindOrganization(orgs)
      navigate(POST_ACTIVATION_REDIRECT_URL)
    } catch (e) {
      console.log("Error creating organization", e)
      setError(await unrollError(e))
      setSubmitting(false)
    }
  }

  return (
    <GradientBox>
      <VStack minWidth="100%" justifyContent="space-between" alignItems="left">
        <Form onSubmit={handleSubmit}>
          <Heading variant="standard">Create an Organization</Heading>
          {sessionLoading && <Spinner mt={4} />}
          {sessionData?.product_name && (
            <Text variant="subtext" mt={2}>
              You&apos;re subscribing to {sessionData.product_name}
            </Text>
          )}
          {error && <Alert status="error">{error}</Alert>}
          <FormControl mt={12} display="flex" flexDirection="column">
            <Input
              variant="account"
              name="name"
              placeholder="Name"
              isRequired
              isDisabled={formBusy}
            />
            <Text variant="subtext">
              A human-readable name for your organization.
            </Text>
            <VStack spacing={6} mt={12}>
              <Button
                variant="primary"
                type="submit"
                title="Create Organization"
                isDisabled={formBusy}
                isLoading={submitting}
              >
                Create
              </Button>
            </VStack>
          </FormControl>
        </Form>
      </VStack>
      <span />
    </GradientBox>
  )
}
